/** Workspace file helpers shared by chat and the file manager. */
export const WORKSPACE = "/home/agent/workspace";

export const fileUrl = (path: string, download = false) =>
  `/api/me/assistant/files/raw?path=${encodeURIComponent(path)}${download ? "&download=1" : ""}`;

export const extOf = (p: string) => p.split(".").pop()?.toLowerCase() ?? "";
export const isImage = (p: string) => ["png", "jpg", "jpeg", "gif", "webp"].includes(extOf(p));
export const isPdf = (p: string) => extOf(p) === "pdf";
export const isText = (p: string) =>
  ["txt", "md", "csv", "json", "log", "py", "ts", "js", "tsx", "jsx", "html", "css", "xml", "yaml", "yml", "sh", "sql", "svg", "toml", "ini", "env"].includes(extOf(p));

export async function uploadFile(file: File, folder = "uploads"): Promise<{ path: string; name: string }> {
  const r = await fetch(`/api/me/assistant/files/upload?path=${encodeURIComponent(folder)}&name=${encodeURIComponent(file.name)}`, {
    method: "POST",
    body: file,
    headers: { "content-type": "application/octet-stream" },
  });
  const body = (await r.json().catch(() => ({}))) as { path?: string; name?: string; error?: string };
  if (!r.ok) throw new Error(body.error ?? `Upload failed (${r.status})`);
  return { path: body.path!, name: body.name! };
}

export const fmtSize = (n: number) => (n < 1024 ? `${n} B` : n < 1024 ** 2 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1024 ** 2).toFixed(1)} MB`);

/** Workspace-relative path from an absolute sandbox path, or null. */
export function workspaceRel(s: string): string | null {
  const t = s.trim().replace(/^`|`$/g, "");
  if (t.startsWith(WORKSPACE + "/")) return t.slice(WORKSPACE.length + 1);
  return null;
}

/** Like uploadFile, with progress (fetch can't report upload progress; XHR can). */
export function uploadWithProgress(file: File, folder: string, onProgress: (fraction: number) => void): Promise<{ path: string; name: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/me/assistant/files/upload?path=${encodeURIComponent(folder)}&name=${encodeURIComponent(file.name)}`);
    xhr.setRequestHeader("content-type", "application/octet-stream");
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      let body: { path?: string; name?: string; error?: string } = {};
      try {
        body = JSON.parse(xhr.responseText);
      } catch {}
      if (xhr.status >= 200 && xhr.status < 300) resolve({ path: body.path!, name: body.name! });
      else reject(new Error(body.error ?? `Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Network error"));
    xhr.send(file);
  });
}

export interface DroppedFile {
  file: File;
  /** Folder inside the drop target, "" for top-level files ("photos/2026" for a dropped folder's file). */
  dir: string;
}

/** Files from a drop, walking into dropped folders (Chrome/Edge/Safari/Firefox all expose webkitGetAsEntry). */
export async function filesFromDrop(dt: DataTransfer): Promise<DroppedFile[]> {
  const entries = Array.from(dt.items)
    .map((i) => (i.kind === "file" ? i.webkitGetAsEntry?.() : null))
    .filter((e): e is FileSystemEntry => !!e);
  if (!entries.length) return Array.from(dt.files).map((file) => ({ file, dir: "" }));
  const out: DroppedFile[] = [];
  const walk = async (entry: FileSystemEntry, dir: string): Promise<void> => {
    if (entry.isFile) {
      const file = await new Promise<File>((res, rej) => (entry as FileSystemFileEntry).file(res, rej));
      out.push({ file, dir });
    } else if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      const sub = dir ? `${dir}/${entry.name}` : entry.name;
      // readEntries returns batches; keep reading until it returns none.
      for (;;) {
        const batch = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
        if (!batch.length) break;
        for (const e of batch) await walk(e, sub);
      }
    }
  };
  for (const e of entries) await walk(e, "");
  return out;
}
